import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { DocumentNode } from 'graphql';
import { Observable, map, tap, filter } from 'rxjs';

export interface InitConnectionOptions {
  query: DocumentNode;
  variables?: { [key: string]: any };
  pageSize?: number;
}

export interface ConnectionOptions {
  after?: string;
  before?: string;
  first?: number;
  last?: number;
  offset?: number;
  orderBy?: string;
  search?: string;
}

export interface PageInfo {
  endCursor: string;
  startCursor: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Add your type name here so the Apollo cache field type policy will accommodate your query.
 *
 *
 * This will usually be the name of the resolver for your query.:
 * ```typescript
 * MyQuery {
 *   caseload { // Use this
 *     ...data
 *   }
 * }
 * ```
 *
 * If a custom field type policy is required, add it to CUSTOM_FIELD_POLICIES in create-type-policy-field.ts
 */
export const RELAY_POLICY_TYPES = [
  'teamCompany',
  'permanentUsers',
  'temporaryUsers',
  'companyApplicationCases',
  'myTodosConnection'
];

/**
 * Provides an easy way to use Apollo Relay Connections
 *
 * ---
 *
 * For better type hinting, pass in a custom QueryOptions interface that extends {@linkcode ConnectionOptions}:
 * ```typescript
 * interface CustomQueryOptions extends ConnectionOptions {
 *   ...your properties here
 * }
 *
 * ...In your component
 * constructor(private apolloRelay: ApolloRelayService<CustomQueryOptions>) {}
 * ```
 * ---
 *
 * **IMPORTANT: There can only be one ApolloRelayService instance per component and it must be added to the component providers**
 *
 */
@Injectable({
  providedIn: 'root'
})
export class ApolloRelayService<T extends ConnectionOptions = unknown> {
  private connection: QueryRef<any>;
  private pageSize: number;
  private nextCursor: string;
  private previousCursor: string;
  private currentConnectionVariables: any = {};

  public hasNextPage: boolean;
  public hasPreviousPage: boolean;
  public responseInfo: any;
  public fromIndex: number = 1;
  public toIndex: number;
  public loading: boolean = false;

  constructor(private apollo: Apollo) {}

  /**
   * Init a new instance of ApolloRelayService
   *
   * **IMPORTANT: There can only be one ApolloRelayService instance per component and it must be added to the component providers**
   *
   * @param options InitConnectionOptions instance
   */
  public initConnection(options: InitConnectionOptions): Observable<any> {
    const { query, variables = {}, pageSize = 5 } = options;
    this.currentConnectionVariables = { ...variables, first: pageSize };
    this.pageSize = pageSize;
    this.toIndex = this.pageSize;
    this.loading = true;
    this.connection = this.apollo.watchQuery({
      query,
      variables: this.currentConnectionVariables,
      nextFetchPolicy: 'cache-first'
    });

    return this.connection.valueChanges.pipe(
      map(({ data }) => {
        if (!data) {
          return null;
        }

        return this.extractResponseData(data);
      }),
      filter((data) => data != null),
      tap(({ edges, pageInfo, ...responseInfo }) => this.extractConnectionInfo(pageInfo, responseInfo)),
      map(({ edges }) => edges.map((edge) => edge.node))
    );
  }

  /**
   * Extract the data from the response
   *
   * The data comes back in a dynamic property set by the query
   * so this extracts the data regardless of the property name
   *
   * @param data The response data
   */
  private extractResponseData(data: any): { [key: string]: any } {
    const key = Object.keys(data)[0];
    const returnData = data[key];

    return returnData;
  }

  /**
   * Extract the connection info from the response data
   *
   * There's information in the response data that doesn't
   * necessarily need to be returned, but is necessary for
   * features such as pagination and sorting
   *
   * @param pageInfo Information about the current page
   * @param responseInfo Optional high level info about the data being returned
   */
  private extractConnectionInfo(pageInfo: PageInfo, responseInfo: any): void {
    this.loading = false;
    this.responseInfo = responseInfo;
    this.nextCursor = pageInfo.endCursor;
    this.previousCursor = pageInfo.startCursor;
    if (this.responseInfo?.totalCount < this.pageSize) {
      this.toIndex = this.responseInfo?.totalCount;
    }
    if (this.hasNextPage === undefined || !this.hasNextPage) {
      this.hasNextPage = pageInfo.hasNextPage;
    }
  }

  /**
   * Update the relay query with new variables
   *
   * This is used for features such as sorting, filtering, and searching
   *
   * @param variables The new parameters to query
   * @param append If true, query will continue using previous variables set. This is useful for searching and sorting
   */
  public updateQuery(variables: T, append: boolean = false): void {
    this.resetQuery(append);
    this.currentConnectionVariables = {
      ...this.currentConnectionVariables,
      ...variables
    };
    this.connection
      .refetch({
        ...this.currentConnectionVariables,
        first: this.pageSize,
        last: null
      })
      .then(() => {
        this.fromIndex = 1;
        this.toIndex = this.responseInfo.totalCount > this.pageSize ? this.pageSize : this.responseInfo.totalCount;
      });
  }

  /**
   * Fetch the next set of results in the current query
   */
  public fetchNext(): void {
    this.loading = true;
    this.currentConnectionVariables.after = this.nextCursor;
    this.currentConnectionVariables.before = null;
    this.currentConnectionVariables.first = this.pageSize;
    this.currentConnectionVariables.last = null;
    this.fromIndex = this.fromIndex + this.pageSize;
    this.connection
      .fetchMore({
        variables: this.currentConnectionVariables
      })
      .then(({ data }) => {
        this.loading = false;
        this.hasPreviousPage = true;
        const key = Object.keys(data)[0];
        this.hasNextPage = data[key].pageInfo.hasNextPage;
        this.toIndex = this.hasNextPage ? this.toIndex + this.pageSize : this.responseInfo.totalCount;
      });
  }

  /**
   * Fetch the previous set of results in the current query
   */
  public fetchPrevious(): void {
    this.loading = true;
    this.currentConnectionVariables.after = null;
    this.currentConnectionVariables.before = this.previousCursor;
    this.currentConnectionVariables.first = null;
    this.currentConnectionVariables.last = this.pageSize;
    this.connection
      .fetchMore({
        variables: this.currentConnectionVariables
      })
      .then(({ data }) => {
        this.loading = false;
        const key = Object.keys(data)[0];
        this.hasPreviousPage = data[key].pageInfo.hasPreviousPage;
        this.hasNextPage = true;
        this.toIndex = this.hasPreviousPage ? this.fromIndex - 1 : this.pageSize;
        this.fromIndex = this.fromIndex - this.pageSize;
      });
  }

  public resetToFirstForNewSelection() {
    // Used to reset to first page when selecting a new option from a drop down menu in connections
    this.fromIndex = 1;
    this.hasPreviousPage = false;
  }

  /**
   * Reset the current query, including variables and page/cursor info
   *
   * @param append If true, the current variables will not be reset (excepting any fields listed in resetFieldsOnAppend)
   */
  private resetQuery(append: boolean = false): void {
    this.hasNextPage = this.hasPreviousPage = undefined;
    this.nextCursor = this.previousCursor = '';
    // We need to append search and orderBy properties
    // so we continue searching/sorting the current query
    // We always want to reset the following fields when
    // updating the query since they have to do with cursors:
    const resetFieldsOnAppend = ['first', 'last', 'after', 'before'];
    Object.keys(this.currentConnectionVariables).forEach((key) => {
      if (!append || resetFieldsOnAppend.includes(key)) {
        this.currentConnectionVariables[key] = null;
      }
    });
  }
}
